import { GENDER } from "../trigger/gender";
import { SKIN_TONES } from "../trigger/skinTones";
import { EYE_COLOR } from "../trigger/eyeColor";
import { HAIR_COLORS } from "../trigger/hairColor";

/**
 * Centralized attribute order configuration
 * This defines the consistent order for model attributes across the entire application
 * Only includes attributes that are actively collected in the current survey flow
 */
export const ATTRIBUTE_ORDER = [
  "gender",
  "skinTone",
  "age",
  "eyeColor",
  "glasses",
  "hairStyle",
  "hairColor",
  "facialHairStyle",
  "facialHairColor",
  "name", // Note: name is included for some experiences but not in model attributes
] as const;

/**
 * Legacy attributes that are no longer collected in the current survey flow
 * These are kept for backward compatibility but should not be used in new code
 */
export const LEGACY_ATTRIBUTES = [
  "hairLength",
  "hairTexture",
  "facialHairLength",
] as const;

export type AttributeKey = (typeof ATTRIBUTE_ORDER)[number];

/**
 * Model attributes type - strict and type-safe
 * This is the ONLY type used throughout the application
 */
export type ModelAttributesCore = {
  gender: (typeof GENDER)[number];
  skinTone: (typeof SKIN_TONES)[number];
  eyeColor: (typeof EYE_COLOR)[number];
  glasses: boolean;
  age?: number;
  hairStyle?: string | null;
  hairColor?: (typeof HAIR_COLORS)[number] | null;
  facialHairStyle?: string | null;
  facialHairColor?: (typeof HAIR_COLORS)[number] | null;

  // Legacy attributes (for backward compatibility)
  hairLength?: string | null;
  hairTexture?: string | null;
  facialHairLength?: string | null;
  hair?: boolean; // Legacy field, might still be in some records
  bodyType?: string; // Legacy field, might still be in some records
  facialHair?: boolean; // Legacy field used in generateModelDescription
};

/**
 * Validation helpers for API inputs
 * These ensure that string inputs from APIs are properly validated before use
 */
export const validateGender = (value: string): (typeof GENDER)[number] => {
  if (GENDER.includes(value as any)) {
    return value as (typeof GENDER)[number];
  }
  throw new Error(
    `Invalid gender: ${value}. Must be one of: ${GENDER.join(", ")}`
  );
};

export const validateSkinTone = (
  value: string
): (typeof SKIN_TONES)[number] => {
  if (SKIN_TONES.includes(value as any)) {
    return value as (typeof SKIN_TONES)[number];
  }
  throw new Error(
    `Invalid skin tone: ${value}. Must be one of: ${SKIN_TONES.join(", ")}`
  );
};

export const validateEyeColor = (value: string): (typeof EYE_COLOR)[number] => {
  if (EYE_COLOR.includes(value as any)) {
    return value as (typeof EYE_COLOR)[number];
  }
  throw new Error(
    `Invalid eye color: ${value}. Must be one of: ${EYE_COLOR.join(", ")}`
  );
};

export const validateHairColor = (
  value: string | null | undefined
): (typeof HAIR_COLORS)[number] | null => {
  if (!value) return null;
  if (HAIR_COLORS.includes(value as any)) {
    return value as (typeof HAIR_COLORS)[number];
  }
  throw new Error(
    `Invalid hair color: ${value}. Must be one of: ${HAIR_COLORS.join(", ")}`
  );
};

/**
 * Validates and converts API input to strict ModelAttributesCore type
 * This should be used in all API endpoints to ensure type safety
 */
export const validateModelAttributes = (input: {
  gender: string;
  skinTone: string;
  eyeColor: string;
  glasses: boolean;
  age?: number;
  hairStyle?: string | null;
  hairColor?: string | null;
  facialHairStyle?: string | null;
  facialHairColor?: string | null;
}): ModelAttributesCore => {
  return {
    gender: validateGender(input.gender),
    skinTone: validateSkinTone(input.skinTone),
    eyeColor: validateEyeColor(input.eyeColor),
    glasses: input.glasses,
    age: input.age,
    hairStyle: input.hairStyle,
    hairColor: validateHairColor(input.hairColor),
    facialHairStyle: input.facialHairStyle,
    facialHairColor: validateHairColor(input.facialHairColor),
  };
};

/**
 * Fetches hair style modifiers using tRPC client
 * @param genderAssociation - Array of gender associations to filter by
 * @param limit - Maximum number of hair styles to return
 * @param trpcClient - tRPC client utils for making API calls
 * @returns Promise<string[]> - Array of hair style values
 */
const getHairStyles = async (
  genderAssociation?: string[],
  limit?: number,
  trpcClient?: any
): Promise<string[]> => {
  if (!trpcClient) {
    throw new Error("tRPC client is required for getHairStyles");
  }

  try {
    const modifiers = await trpcClient.modifiers.get.fetch({
      category: "hairStyle",
      genderAssociation,
      limit: limit || 50,
    });

    // Extract the values from modifiers, filtering out null/undefined values
    return modifiers
      .map((modifier: any) => modifier.value)
      .filter(
        (value: any): value is string => value !== null && value !== undefined
      );
  } catch (error) {
    console.error("Error fetching hair styles:", error);
    throw error;
  }
};

/**
 * Fetches facial hair style modifiers using tRPC client
 * @param genderAssociation - Array of gender associations to filter by
 * @param limit - Maximum number of facial hair styles to return
 * @param trpcClient - tRPC client utils for making API calls
 * @returns Promise<string[]> - Array of facial hair style values
 */
const getFacialHairStyles = async (
  genderAssociation?: string[],
  limit?: number,
  trpcClient?: any
): Promise<string[]> => {
  if (!trpcClient) {
    throw new Error("tRPC client is required for getFacialHairStyles");
  }

  try {
    const modifiers = await trpcClient.modifiers.get.fetch({
      category: "facialHairStyle",
      genderAssociation,
      limit: limit || 50,
    });

    // Extract the values from modifiers, filtering out null/undefined values
    return modifiers
      .map((modifier: any) => modifier.value)
      .filter(
        (value: any): value is string => value !== null && value !== undefined
      );
  } catch (error) {
    console.error("Error fetching facial hair styles:", error);
    throw error;
  }
};

/**
 * Model attribute options - only includes attributes that are actively collected in the current survey flow
 *
 * ACTIVE ATTRIBUTES (collected in survey):
 * - gender, skinTone, age, eyeColor, glasses, hairStyle, hairColor, facialHairStyle, facialHairColor, name
 *
 * LEGACY ATTRIBUTES (no longer collected, kept for backward compatibility):
 * - hairLength, hairTexture, facialHairLength
 */
export const MODEL_ATTRIBUTE_OPTIONS = {
  gender: GENDER, // gender-neutral
  skinTone: SKIN_TONES, // gender-neutral
  age: Array.from({ length: 103 }, (_, i) => i + 18), // 18-120 years old, gender-neutral
  eyeColor: EYE_COLOR, // gender-neutral
  hairColor: HAIR_COLORS, // gender-neutral
  hairStyle: getHairStyles, // dynamic based on gender
  facialHairStyle: getFacialHairStyles, // dynamic based on gender (male only)
  facialHairColor: HAIR_COLORS, // male only
  glasses: [true, false], // gender-neutral
};
